import { useOutletContext, useParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { format } from "date-fns";
import { BugIcon, CheckCircle2Icon, CircleDotIcon, ClockIcon, LockIcon, TicketIcon, TrendingUpIcon, UsersIcon } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, XAxis, YAxis } from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";

import { ChartSkeleton, LegendSkeleton } from "@/features/projects/components/ChartSkeleton";
import { RecentActivityRow } from "@/features/projects/components/RecentActivityRow";
import { RecentActivityRowSkeleton } from "@/features/projects/components/RecentActivityRowSkeleton";
import { OVERVIEW_MEMBER_ACTIVITY_CONFIG, OVERVIEW_PRIORITY_CONFIG, OVERVIEW_STATUS_COLOURS, OVERVIEW_STATUS_CONFIG, OVERVIEW_STATUS_KEYS, OVERVIEW_TICKET_TREND_CONFIG } from "@/features/projects/config";
import { summaryPersonShortName } from "@/features/projects/helpers";
import { getProjectSummary } from "@/features/projects/services/project";
import { Project, ProjectSummary } from "@/features/projects/types";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { StatCard, StatCardSkeleton } from "@/shared/components/StatCard";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

export default function ProjectOverview() {
  const { id } = useParams();
  const { permissions } = useOutletContext() as { project: Project; permissions: string[] };

  const canReadProject = permissions.includes(PERMISSIONS.PROJECT_READ);
  const canReadTickets = permissions.includes(PERMISSIONS.PROJECT_TICKETS_READ);

  const {
    data: summary,
    isPending,
    error,
    refetch,
  } = useQuery<ProjectSummary, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_PROJECT_SUMMARY, id],
    queryFn: () => getProjectSummary(id!),
    enabled: canReadProject,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  const stats = summary?.stats;
  const recentActivity = summary?.recentActivity ?? [];

  const trendData = (summary?.ticketTrend ?? []).map((point) => ({
    month: format(new Date(point.year, point.month - 1, 1), "MMM"),
    created: point.createdCount,
    resolved: point.resolvedCount,
  }));

  const statusData = (summary?.statusBreakdown ?? []).map((entry) => ({
    status: OVERVIEW_STATUS_KEYS[entry.status],
    label: entry.status,
    value: entry.count,
    fill: OVERVIEW_STATUS_COLOURS[entry.status],
  }));

  const priorityData = (summary?.priorityBreakdown ?? []).map((entry) => ({
    priority: entry.priority,
    count: entry.count,
  }));

  const memberActivityData = (summary?.memberActivity ?? []).map((entry) => ({
    name: summaryPersonShortName(entry.firstName, entry.lastName),
    closed: entry.closedCount,
  }));

  const hasTrend = trendData.some((point) => point.created > 0 || point.resolved > 0);
  const hasStatuses = statusData.some((entry) => entry.value > 0);
  const hasPriorities = priorityData.some((entry) => entry.count > 0);
  const hasMemberActivity = memberActivityData.some((entry) => entry.closed > 0);

  if (!canReadProject) {
    return <EmptyState icon={LockIcon} title="No access" description="You don't have permission to view this project's overview." className="border border-dashed" />;
  }

  if (error && !errorModalOpen) {
    return (
      <>
        <Error variant="modal" title="Failed to load overview" error={error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />
        <InlineError title="Failed to load overview" description="Something went wrong while fetching this project's summary." onRetry={handleRetry} className="border border-dashed" />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {error && <Error variant="modal" title="Failed to load overview" error={error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isPending || !stats ? (
          Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard label="Open tickets" value={stats.openTicketCount} sub={`+${stats.openedThisWeekCount} this week`} icon={BugIcon} />
            <StatCard label="In progress" value={stats.inProgressTicketCount} sub={`${stats.inProgressUnassignedCount} unassigned`} icon={CircleDotIcon} />
            <StatCard label="Resolved" value={stats.resolvedTicketCount} sub={`+${stats.resolvedThisMonthCount} this month`} icon={CheckCircle2Icon} />
            <StatCard label="Members" value={stats.memberCount} sub={`${stats.adminMemberCount} admin${stats.adminMemberCount !== 1 ? "s" : ""}`} icon={UsersIcon} />
          </>
        )}
      </div>

      {/* Ticket trend & status breakdown */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUpIcon className="size-4 text-primary" />
              <CardTitle>Ticket trend</CardTitle>
            </div>
            <CardDescription>Tickets created vs resolved over the last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            {isPending ? (
              <ChartSkeleton className="h-[220px]" />
            ) : !hasTrend ? (
              <EmptyState icon={TrendingUpIcon} title="No activity yet" description="No tickets have been created or resolved in the last 6 months." className="py-12" />
            ) : (
              <ChartContainer config={OVERVIEW_TICKET_TREND_CONFIG} className="h-[220px] w-full">
                <LineChart accessibilityLayer data={trendData}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} width={28} allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Line type="monotone" dataKey="created" stroke="var(--color-created)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="resolved" stroke="var(--color-resolved)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Status breakdown</CardTitle>
            <CardDescription>Current distribution of all tickets</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            {isPending ? (
              <>
                <ChartSkeleton className="h-[180px]" />
                <LegendSkeleton rows={5} />
              </>
            ) : !hasStatuses ? (
              <EmptyState icon={TicketIcon} title="No tickets yet" description="Ticket statuses will appear here once work is raised." className="py-12" />
            ) : (
              <>
                <ChartContainer config={OVERVIEW_STATUS_CONFIG} className="h-[180px] w-full">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="status" />} />
                    <Pie data={statusData} dataKey="value" nameKey="label" innerRadius="55%" outerRadius="80%" paddingAngle={2} />
                  </PieChart>
                </ChartContainer>
                <div className="flex w-full flex-col gap-2">
                  {statusData.map((entry) => (
                    <div key={entry.status} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="size-2 shrink-0" style={{ backgroundColor: entry.fill }} />
                        <span className="text-muted-foreground">{entry.label}</span>
                      </div>
                      <span className="font-semibold tabular-nums">{entry.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Priority & member activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Tickets by priority</CardTitle>
            <CardDescription>Open ticket count across priority levels</CardDescription>
          </CardHeader>
          <CardContent>
            {isPending ? (
              <ChartSkeleton />
            ) : !hasPriorities ? (
              <EmptyState icon={TicketIcon} title="No open tickets" description="Every ticket in this project has been resolved or closed." className="py-12" />
            ) : (
              <ChartContainer config={OVERVIEW_PRIORITY_CONFIG} className="h-[200px] w-full">
                <BarChart accessibilityLayer data={priorityData} layout="vertical" margin={{ left: 0 }}>
                  <CartesianGrid horizontal={false} />
                  <XAxis type="number" tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                  <YAxis type="category" dataKey="priority" tickLine={false} axisLine={false} tickMargin={8} width={56} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" radius={2}>
                    {priorityData.map((entry) => (
                      <Cell key={entry.priority} fill={`var(--color-${entry.priority})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Member activity</CardTitle>
            <CardDescription>Tickets closed per member this month</CardDescription>
          </CardHeader>
          <CardContent>
            {isPending ? (
              <ChartSkeleton />
            ) : !hasMemberActivity ? (
              <EmptyState icon={UsersIcon} title="No closures this month" description="Tickets resolved or closed this month will show up here." className="py-12" />
            ) : (
              <ChartContainer config={OVERVIEW_MEMBER_ACTIVITY_CONFIG} className="h-[200px] w-full">
                <BarChart accessibilityLayer data={memberActivityData}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} width={28} allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="closed" fill="var(--color-closed)" radius={2} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent activity */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center gap-2">
            <ClockIcon className="size-4 text-primary" />
            <CardTitle>Recent activity</CardTitle>
          </div>
          <CardDescription>Latest updates across this project</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isPending ? (
            <ul className="flex flex-col">
              {Array.from({ length: 5 }).map((_, i) => (
                <li key={i}>
                  <RecentActivityRowSkeleton />
                  {i < 4 && <Separator />}
                </li>
              ))}
            </ul>
          ) : recentActivity.length === 0 ? (
            <EmptyState icon={ClockIcon} title="No activity yet" description="Ticket creations and status changes will appear here." className="border border-dashed m-4" />
          ) : (
            <ul className="flex flex-col">
              {recentActivity.map((activity, i) => (
                <li key={`${activity.type}-${activity.ticketId}-${activity.occurredAt.getTime()}`}>
                  <RecentActivityRow activity={activity} projectId={id!} canReadTickets={canReadTickets} />
                  {i < recentActivity.length - 1 && <Separator />}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
